import { bottomBarHeight, leftSidebarToolsWidth, rightSidebarWidth } from 'src/Constants'
import { BaseAction, CallResult, DataAction, MessageAction } from 'src/CustomTypes'
import { AddressResult } from 'src/dataSources/dataforsyningen/Address'
import { GeoModel } from 'src/dataSources/geo/GeoModel'
import { MapLegendObject } from 'src/dataSources/geo/MapLegend'
import { Graph, GraphGroup } from 'src/dataSources/graphs/GraphRegistry'
import { DataTheme, ToolType } from 'src/Enums'
import {
  ADD_GRAPH_GROUP,
  REMOVE_GRAPH_GROUP,
  SET_DATA_THEME,
  SET_SELECTED_GEO_MODEL,
  SET_SELECTED_GRAPH,
  SET_SELECTED_TOOL,
  FETCHING_GEO_MODELS_STARTED,
  FETCHING_GEO_MODELS_FAILURE,
  FETCHING_GEO_MODELS_SUCCESS,
  FETCHING_MAP_LEGEND_STARTED,
  FETCHING_MAP_LEGEND_FAILURE,
  FETCHING_MAP_LEGEND_SUCCESS,
  SET_SHOW_LEFT_SIDE_BAR,
  SET_ADDRESSES,
  SET_ADDRESS_QUERY,
  FETCHING_ADDRESSES_STARTED,
  FETCHING_ADDRESSES_FAILURE,
  FETCHING_ADDRESSES_SUCCESS,
  SET_BOTTOM_BAR_HEIGHT,
  SET_BOTTOM_BAR_WIDTH,
  SET_RIGHT_SIDE_BAR_WIDTH,
  SET_RIGHT_SIDE_BAR_DRAGGING,
  SET_BOTTOM_BAR_DRAGGING
} from '../actions/GeneralActions'

export interface GeneralState {
  addressQuery: string
  addresses: AddressResult[]
  bottomBarWidth: number
  bottomBarHeight: number
  bottomBarDragging: Boolean
  dataTheme: DataTheme
  enabledGraphGroups: GraphGroup[]
  fetchAddressesStatus: CallResult
  fetchAddressesError: string
  fetchGeoModelsStatus: CallResult
  fetchGeoModelsError: string
  fetchMapLegendStatus: CallResult
  fetchMapLegendError: string
  geoModels?: GeoModel[]
  mapLegend?: MapLegendObject[]
  rightSideBarDragging: Boolean
  rightSideBarWidth: number
  selectedGeoModel?: GeoModel
  selectedGraph?: Graph
  selectedTool: ToolType
  showLeftSideBar: boolean
}

export const generalStateInitial: GeneralState = {
  addressQuery: '',
  addresses: [],
  bottomBarHeight: bottomBarHeight,
  bottomBarWidth: window.innerWidth - leftSidebarToolsWidth,
  bottomBarDragging: false,
  dataTheme: DataTheme.NONE,
  enabledGraphGroups: [],
  fetchAddressesStatus: CallResult.Initial,
  fetchAddressesError: '',
  fetchGeoModelsStatus: CallResult.Initial,
  fetchGeoModelsError: '',
  fetchMapLegendStatus: CallResult.Initial,
  fetchMapLegendError: '',
  geoModels: undefined,
  mapLegend: undefined,
  rightSideBarWidth: rightSidebarWidth,
  rightSideBarDragging: false,
  selectedGeoModel: undefined,
  selectedTool: ToolType.INFO,
  selectedGraph: undefined,
  showLeftSideBar: true
}

export function generalReducer (
  state: GeneralState = generalStateInitial, // eslint-disable-line @typescript-eslint/default-param-last
  action: BaseAction): GeneralState {
  switch (action.type) {
    case ADD_GRAPH_GROUP: {
      const a = action as DataAction<GraphGroup>
      let graphGroups = [...state.enabledGraphGroups]
      const graphGroupAlreadyEnabled = state.enabledGraphGroups.some(graphGroup => graphGroup === a.data)
      if (!graphGroupAlreadyEnabled) {
        graphGroups = [...state.enabledGraphGroups, a.data]
      }
      return {
        ...state,
        enabledGraphGroups: graphGroups
      }
    }
    case REMOVE_GRAPH_GROUP: {
      const a = action as DataAction<GraphGroup>
      return {
        ...state,
        enabledGraphGroups: state.enabledGraphGroups.filter(graphGroup => graphGroup !== a.data)
      }
    }
    case FETCHING_ADDRESSES_STARTED: {
      return {
        ...state,
        fetchAddressesStatus: CallResult.Running
      }
    }
    case FETCHING_ADDRESSES_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchAddressesStatus: CallResult.Failed,
        fetchAddressesError: a.message
      }
    }
    case FETCHING_ADDRESSES_SUCCESS: {
      const a = action as DataAction<AddressResult[]>
      return {
        ...state,
        fetchAddressesStatus: CallResult.Succeeded,
        addresses: a.data
      }
    }
    case FETCHING_GEO_MODELS_STARTED: {
      return {
        ...state,
        fetchGeoModelsStatus: CallResult.Running
      }
    }
    case FETCHING_GEO_MODELS_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchGeoModelsStatus: CallResult.Failed,
        fetchGeoModelsError: a.message
      }
    }
    case FETCHING_GEO_MODELS_SUCCESS: {
      const a = action as DataAction<GeoModel[] | null>
      return {
        ...state,
        fetchGeoModelsStatus: CallResult.Succeeded,
        geoModels: a.data !== null ? a.data : undefined
      }
    }
    case FETCHING_MAP_LEGEND_STARTED: {
      return {
        ...state,
        fetchMapLegendStatus: CallResult.Running
      }
    }
    case FETCHING_MAP_LEGEND_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchMapLegendStatus: CallResult.Failed,
        fetchMapLegendError: a.message
      }
    }
    case FETCHING_MAP_LEGEND_SUCCESS: {
      const a = action as DataAction<MapLegendObject[] | null>
      return {
        ...state,
        fetchMapLegendStatus: CallResult.Succeeded,
        mapLegend: a.data !== null ? a.data : undefined
      }
    }
    case SET_ADDRESS_QUERY: {
      const a = action as DataAction<string>
      return {
        ...state,
        addressQuery: a.data
      }
    }
    case SET_ADDRESSES: {
      const a = action as DataAction<AddressResult[]>
      return {
        ...state,
        addresses: a.data
      }
    }
    case SET_BOTTOM_BAR_HEIGHT: {
      const a = action as DataAction<number>
      return {
        ...state,
        bottomBarHeight: a.data
      }
    }
    case SET_BOTTOM_BAR_WIDTH: {
      const a = action as DataAction<number>
      return {
        ...state,
        bottomBarWidth: a.data
      }
    }
    case SET_BOTTOM_BAR_DRAGGING: {
      const a = action as DataAction<Boolean>
      return {
        ...state,
        bottomBarDragging: a.data
      }
    }
    case SET_DATA_THEME: {
      const a = action as DataAction<DataTheme>
      return {
        ...state,
        dataTheme: a.data
      }
    }
    case SET_RIGHT_SIDE_BAR_DRAGGING: {
      const a = action as DataAction<Boolean>
      return {
        ...state,
        rightSideBarDragging: a.data
      }
    }
    case SET_RIGHT_SIDE_BAR_WIDTH: {
      const a = action as DataAction<number>
      return {
        ...state,
        rightSideBarWidth: a.data
      }
    }
    case SET_SELECTED_GEO_MODEL: {
      const a = action as DataAction<GeoModel>
      return {
        ...state,
        selectedGeoModel: a.data
      }
    }
    case SET_SELECTED_GRAPH: {
      const a = action as DataAction<Graph>
      return {
        ...state,
        selectedGraph: a.data
      }
    }
    case SET_SELECTED_TOOL: {
      const a = action as DataAction<ToolType>
      return {
        ...state,
        selectedTool: a.data
      }
    }
    case SET_SHOW_LEFT_SIDE_BAR: {
      const a = action as DataAction<boolean>
      return {
        ...state,
        showLeftSideBar: a.data
      }
    }
    default:
      return state
  }
}
